/**
 * The hosting surface for a contract-v2 simulation.
 *
 * Everything on screen is built from the manifest the simulation posts on load.
 * This component knows nothing about slits or projectiles, and it must stay that
 * way: the moment it needs to special-case an id, the contract is missing
 * something and the contract is what should change.
 *
 * Shared rather than written per app for the same reason the bench is: the
 * student app and the public science site should not drift into two different
 * control designs for the same thirty-seven simulations.
 *
 * See docs/FeatureDesigns/simulations/simulation-contract-target.md.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import './SimulationPlayer.css';
import { FullscreenIcon } from '../components/FullscreenIcon';
import { ThemeIcon } from '../components/PaneButtons';

export interface SimParam {
  key: string; label: string; symbol?: string; unit: string;
  min: number; max: number; step: number; value: number;
  /** Shown only while every named mode has this value — a slider that
   *  belongs to one set-up of the simulation and means nothing in another. */
  when?: Record<string, string>;
}
export interface SimMode {
  key: string; label: string; value: string;
  options: { value: string; label: string }[];
  /**
   * Several at once, rather than one of several.
   *
   * The value is then the chosen options joined by commas — a string either
   * way, so nothing else in the contract changes: the message that carries it,
   * the runtime that receives it and the simulations that never asked for it
   * all stay as they are.
   *
   * A rolling race is what this is for: "all four / solid / hollow" made the
   * reader pick from the comparisons somebody else thought of, when the
   * question is which bodies to put on the ramp.
   */
  multi?: boolean;
}
export interface SimReadout {
  key: string; label: string; unit: string; formula?: string; primary?: boolean;
}
/**
 * Where a narrated simulation's voices are, and what they are narrating.
 *
 * `capabilities.narration` has always been a yes/no, and a yes/no cannot be
 * played: the two explainers ship eight and four pre-rendered tracks apiece
 * and nothing on either surface knew the files existed. This says where the
 * voice manifest is — resolved against the simulation's own URL, so a
 * simulation still owns its assets — and which control the clips follow.
 *
 * `track` names a param or a mode whose value is the clip number, 1-based:
 * scene 3 is `s3.mp3` and `dur[2]`. That indirection is what keeps the player
 * ignorant of scenes; it plays the n-th clip of whatever the simulation calls
 * its steps.
 */
export interface SimNarration {
  /** Voice manifest, relative to the simulation's URL. */
  src: string;
  /** The param or mode whose value selects the clip. */
  track: string;
  /** Step on when a clip ends, so the walkthrough plays through. */
  advance?: boolean;
}
interface Voice {
  key: string; label: string; lang: string; register: string; dur: number[];
}
export interface SimCapabilities {
  orientations: ('landscape' | 'portrait')[];
  minStage: { w: number; h: number };
  animates: boolean;
  pointer: 'none' | 'tap' | 'drag';
  themes: ('dark' | 'light')[];
  present: boolean;
  narration: boolean;
  reducedMotionSafe: boolean;
}
export interface SimManifest {
  version: 1; id: string; title: string;
  /** The box shape it would choose, given the choice — see the CSS on
   *  `data-fit`. A preference only: it must still read in any box. */
  fit?: 'wide' | 'tall' | 'any';
  capabilities: SimCapabilities;
  params?: SimParam[]; modes?: SimMode[]; readouts?: SimReadout[];
  presets?: { label: string; values: Record<string, number | string> }[];
  narration?: SimNarration;
}

interface Props {
  /** Absolute or root-relative URL of the v2 asset. */
  src: string;
  title: string;
  /** Rendered at the left of the top bar — each app's own way back. */
  onBack?: () => void;
  /** Shown when the manifest fails validation. Never in production. */
  showProblems?: boolean;
  /**
   * The player ALREADY has the whole viewport — there is nothing to expand
   * into.
   *
   * `/play/:id` is the simulation as an instrument: the page is the player and
   * nothing else. The full-screen control there had no room to give and only
   * one visible effect left, hiding the panel — so the shrink arrow appeared
   * to toggle the controls, which is the one confusion this transport was
   * designed to avoid ("filling the screen and hiding the controls are two
   * different things"). A surface that is already filling says so, and the
   * control stands down rather than doing the wrong job.
   */
  filling?: boolean;
  /**
   * What LEAVING means on a surface that is already filling the screen.
   *
   * The shrink arrow still has an honest job there, and it is the one a reader
   * expects of it: get me out of this thing and back into the site. On
   * `/play/:id` that is the simulation's own page — the same instrument with
   * the masthead, the essay and somewhere to go next around it. Without this
   * the control is simply not drawn, because an arrow that shrinks nothing is
   * worse than no arrow.
   */
  onLeaveFull?: () => void;
}

/** A preset's label as it appears in a link: "Disc vs ball" → "disc-vs-ball". */
export const presetSlug = (label: string): string =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const DECIMALS = (step: number): number =>
  step >= 1 ? 0 : (String(step).split('.')[1]?.length ?? 0);

/** A value with its unit as it is written: "50 m", but "20°" (an angle's
 *  sign sits on the number) and "0.30" (a dimensionless quantity has no unit
 *  to show — the manifest's '—' only says there is none). */
const withUnit = (n: string, unit?: string): string =>
  !unit || unit === '—' || unit === '-' ? n
  : unit === '°' || unit === '%' ? `${n}${unit}`
  : `${n} ${unit}`;
const fmt = (p: SimParam, v: number): string => withUnit(v.toFixed(DECIMALS(p.step)), p.unit);

export function SimulationPlayer({ src, title, onBack, showProblems, filling, onLeaveFull }: Props): React.JSX.Element {
  const root = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [manifest, setManifest] = useState<SimManifest | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, number | string>>({});

  /**
   * Open, not collapsed, to start with. A simulation whose knobs are hidden
   * behind a summary line is a picture — most readers never discover there was
   * anything to turn. The line is still there to collapse it, and the ✕ still
   * removes it; the default just stops making discovery the reader's problem.
   */
  const [panel, setPanel] = useState<'peek' | 'open'>('open');
  /** Hidden outright, as opposed to collapsed to its grip. Fullscreen starts
   *  this way so the stage gets the whole screen, and ⚙ brings it back. */
  const [panelHidden, setPanelHidden] = useState(false);
  const [full, setFull] = useState(false);
  const fullRef = useRef(false);
  useEffect(() => { fullRef.current = full; }, [full]);
  const [playing, setPlaying] = useState(true);
  /** Whether the STAGE is running light — which is a decision about the page
   *  and the simulation together (see lightSurface and the theme message), and
   *  which the player's own chrome then has to follow. It did not, so a pale
   *  bench sat in a black frame beside a black panel. */
  const [stageLight, setStageLight] = useState(false);

  const [landscape, setLandscape] = useState(false);

  /**
   * "Show all labels" — on: every label the simulation has, however small the
   * box; off: the answer and its causes, with the names of the parts gone,
   * however big.
   *
   * Until the reader chooses, the ROOM decides, exactly as it did before
   * there was a switch: the runtime reports what fits (`autoDetail`, 3 =
   * everything) and the box shows that. A choice, once made, is remembered
   * per reader like the theme — someone who wants the names of things wants
   * them in every simulation, and someone who switched them off does not
   * want them back on the next one.
   */
  const [labelsChoice, setLabelsChoice] = useState<'all' | 'few' | null>(() => {
    try { const v = localStorage.getItem('simplayer.labels'); return v === 'all' || v === 'few' ? v : null; } catch { return null; }
  });
  const [autoDetail, setAutoDetail] = useState(3);
  const labelsChoiceRef = useRef(labelsChoice);
  useEffect(() => { labelsChoiceRef.current = labelsChoice; }, [labelsChoice]);
  const labelsAll = labelsChoice ? labelsChoice === 'all' : autoDetail >= 3;
  const [notice, setNotice] = useState<{ text: string; action?: [string, () => void] } | null>(null);

  /** The voices a narrated simulation ships, and which one is speaking. 'off'
   *  is a real choice, and the one it starts on: a page that talks the moment
   *  it loads is a page people close. */
  const [voices, setVoices] = useState<Voice[]>([]);
  const [voice, setVoice] = useState<string>('off');
  const audio = useRef<HTMLAudioElement | null>(null);
  /** Directory the clips sit in — `<dir>/<voice>/s<n>.mp3`. */
  const clipDir = useRef<string>('');

  const send = useCallback((msg: Record<string, unknown>) => {
    frame.current?.contentWindow?.postMessage(msg, '*');
  }, []);

  /**
   * Which ground this simulation is read on.
   *
   * Remembered per reader, not per simulation: someone reading in a bright
   * room wants every simulation that can go light to be light, and having to
   * say so again on each one is the control working against them. Simulations
   * that cannot go light (a diffraction fringe, a glowing gas) simply never
   * offer it — see the manifest's `themes`.
   */
  const readStoredTheme = (): 'light' | 'dark' | null => {
    try {
      const v = localStorage.getItem('simplayer.theme');
      return v === 'light' || v === 'dark' ? v : null;
    } catch { return null; }
  };

  /** Hand the palette down and take the frame with it — the panel, the bar and
   *  the transport all read the same tokens, so this is the whole of it. */
  const applyTheme = useCallback((light: boolean): void => {
    send(light
      ? { type: 'theme', theme: 'light', ink: '#16202e', muted: 'rgba(22,32,46,0.6)', ground: '#f4f7fb' }
      : { type: 'theme', theme: 'dark', ink: '#e9edf9', muted: 'rgba(233,237,249,0.55)', ground: '#05070c' });
    setStageLight(light);
  }, [send]);

  const toggleTheme = (): void => {
    const next = !stageLight;
    applyTheme(next);
    try { localStorage.setItem('simplayer.theme', next ? 'light' : 'dark'); } catch { /* not persisted */ }
  };

  /** What the page around us is showing — an explicit data-theme wins, and
   *  otherwise the OS setting, which is exactly how the site's own CSS
   *  decides. */
  const lightSurface = useCallback((): boolean => {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'light') return true;
    if (attr === 'dark') return false;
    return !window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // ── the conversation ──────────────────────────────────────────
  useEffect(() => {
    const onMessage = (e: MessageEvent): void => {
      const d = e.data as { source?: string; type?: string; manifest?: SimManifest; problems?: string[]; values?: Record<string, number>; auto?: number };
      if (!d || d.source !== 'zitmus-sim') return;
      if (d.type === 'detail' && typeof d.auto === 'number') { setAutoDetail(d.auto); return; }
      if (d.type === 'ready' && d.manifest) {
        const m = d.manifest;
        const initial: Record<string, number | string> = {};
        (m.params ?? []).forEach(p => { initial[p.key] = p.value; });
        (m.modes ?? []).forEach(x => { initial[x.key] = x.value; });
        setManifest(m);
        setValues(initial);
        setProblems(d.problems ?? []);
        // A simulation that says it is meaningless still needs an explicit
        // choice from someone who has asked for less motion.
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        setPlaying(!(reduced && m.capabilities.reducedMotionSafe === false));

        /**
         * Hand down the palette, if this simulation says it can take one.
         *
         * `themes` was declared by every simulation and read by nobody: the
         * runtime has always accepted a theme message and the host had never
         * sent one, so a light page framed a dark rectangle whatever the
         * manifest claimed. Now the ones that opt in follow the page, and the
         * ones that genuinely need a dark ground — a diffraction fringe, a
         * glowing gas — keep it by simply not listing 'light'.
         */
        if ((m.capabilities.themes ?? []).includes('light')) {
          // A remembered choice wins over the page: someone who turned this
          // simulation light did so about THIS simulation, and a dark page
          // around it does not un-decide that.
          const stored = readStoredTheme();
          if (stored === 'light' || (stored === null && lightSurface())) applyTheme(true);
        }

        /**
         * And say which edges we have covered. The player puts its transport
         * buttons across the top of the stage and, in portrait, nothing at the
         * foot; a simulation cannot see that and was writing its readouts
         * underneath them.
         */
        send({ type: 'safe', top: 52, right: 8, bottom: 8, left: 8 });

        if (labelsChoiceRef.current) send({ type: 'labels', all: labelsChoiceRef.current === 'all' });

        /**
         * `?preset=disc-vs-ball` opens on one of the simulation's own Try
         * presets, named by its label.
         *
         * A link from outside — an ad card that ends on "disc or ball?" — has
         * to land on the comparison it asked about, not on whatever the
         * simulation shows first. Only declared presets can be named, so a
         * link cannot put the simulation in a state its own panel could not
         * reach; an unknown name opens the defaults. Sending it also stands the
         * simulation's tour down, so the reader arrives to a still instrument.
         */
        const wanted = new URLSearchParams(window.location.search).get('preset');
        const preset = wanted ? (m.presets ?? []).find(pr => presetSlug(pr.label) === wanted) : undefined;
        if (preset) {
          setValues({ ...initial, ...preset.values });
          send({ type: 'setMany', values: preset.values });
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [send, lightSurface]);

  /**
   * The player follows the shape of ITS OWN BOX, not the device.
   *
   * Asking the window meant that turning a phone sideways re-laid-out a player
   * sitting in the middle of a scrolling page: the page was still a portrait
   * document with a portrait-shaped box in it, but the controls jumped into a
   * side rail and the simulation turned with them. Embedded, the box stays the
   * shape the page gives it; fullscreen, the box IS the device, so rotating
   * does what you would expect.
   *
   * It also fixes the quieter version of the same thing: a narrow box on a wide
   * desktop window was being told it was landscape.
   */
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = (): void => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const wide = r.width > r.height;
      // On a phone, a player sitting in a page stays portrait even when the
      // phone is turned: the page is still a portrait document being read, and
      // re-laying out the stage mid-read is disorienting for no gain. Turning
      // the device is a request about the DEVICE, and fullscreen is where that
      // request means something. A desktop page is wide enough that the rail
      // is simply the right layout, so it is not caught by this.
      // The SHORTER side identifies the device: a phone is ~400 either way up,
      // where a laptop is 800+ even in its narrow dimension. Testing the width
      // alone would call a rotated phone a desktop, which is the case this
      // whole rule exists for.
      const smallDevice = Math.min(window.innerWidth, window.innerHeight) < 640;
      const embeddedOnAPhone = !document.fullscreenElement && !fullRef.current && smallDevice;
      setLandscape(wide && !embeddedOnAPhone);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { send({ type: playing ? 'play' : 'pause' }); }, [playing, send]);

  /**
   * Fullscreen is requested on the player, not on the iframe — the controls are
   * ours and have to come with it. Entering also changes the box, which the
   * simulation treats as any other resize and recomposes for.
   */
  const applyFullState = (isFull: boolean): void => {
    setFull(isFull);
    // The embedded-on-a-phone rule above depends on this, so the box has to
    // be re-read rather than waiting for a resize that may not come.
    const r = root.current?.getBoundingClientRect();
    const big = Math.min(window.innerWidth, window.innerHeight) >= 640;
    if (r && r.width > 0) setLandscape(r.width > r.height && (isFull || big));
    // A stage worth filling starts unobstructed; ⚙ is right there.
    setPanelHidden(isFull);
  };

  useEffect(() => {
    const onChange = (): void => applyFullState(document.fullscreenElement === root.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = (): void => {
    /* LEAVING is decided by our own state, not by the browser's.
     *
     * There are two ways to be full — the real one, and the faked one this
     * falls back to — and the exit has to match the way in. Asking
     * `document.fullscreenElement` instead conflates them: a player that went
     * full by faking it has no fullscreen element, so the check said "not
     * full", and the button that plainly reads "leave" asked to ENTER again.
     * The reader presses the shrink arrow and nothing shrinks.
     *
     * That state is reachable whenever a real request is refused — inside a
     * frame without the permission, or under a policy that declines it — and
     * the catch below is exactly what puts us there.
     */
    if (fullRef.current) {
      if (document.fullscreenElement) void document.exitFullscreen();
      else applyFullState(false);
      return;
    }
    /* iPhone Safari has never implemented `Element.requestFullscreen` for a
     * plain element — only iPadOS and every other browser do. There the real
     * request silently does nothing and the button reads as broken, so we
     * fake it: `[data-full="true"]` alone is enough to cover the viewport,
     * and `fullscreenchange` simply never fires to drive it. */
    const supportsFullscreen = typeof root.current?.requestFullscreen === 'function';
    if (!supportsFullscreen) { applyFullState(true); return; }
    void root.current?.requestFullscreen?.().catch(() => applyFullState(true));
  };

  /**
   * The capabilities, acted on. Every branch degrades — none refuses — because
   * a reader must never meet a blank frame over a declaration.
   */
  useEffect(() => {
    if (!manifest) return;
    const caps = manifest.capabilities;
    const want = landscape ? 'landscape' : 'portrait';
    if (!caps.orientations.includes(want)) {
      setNotice({ text: `Turn your phone — this one is composed for ${caps.orientations[0]}.` });
      return;
    }
    const box = stage.current?.getBoundingClientRect();
    if (box && (box.width < caps.minStage.w || box.height < caps.minStage.h)) {
      // Give up our own panel before telling anyone the screen is too small.
      if (panel === 'open') { setPanel('peek'); return; }
      setNotice({ text: 'Not much room — hide the controls for a better look.', action: ['Hide', () => { setPanelHidden(true); setNotice(null); }] });
      return;
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced && caps.reducedMotionSafe === false && !playing) {
      setNotice({ text: 'This one only makes sense moving.', action: ['Play anyway', () => { setPlaying(true); setNotice(null); }] });
      return;
    }
    setNotice(null);
  }, [manifest, landscape, panel, playing]);

  /**
   * Fetch the voice manifest, once the simulation has said it has one.
   *
   * Resolved against the simulation's own URL rather than the page's: the
   * asset lives beside the simulation (`../audio/river-boat/voices.json`) and
   * the player is mounted from three different routes in two apps.
   */
  useEffect(() => {
    const n = manifest?.narration;
    if (!n || !manifest?.capabilities?.narration) { setVoices([]); return; }
    let live = true;
    const url = new URL(n.src, new URL(src, window.location.href));
    clipDir.current = url.href.replace(/\/[^/]*$/, '');
    fetch(url.href)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: { voices?: Voice[] }) => { if (live) setVoices(j.voices ?? []); })
      // A missing track is not an error worth a banner: the simulation still
      // reads with the sound off, which is what its on-stage gloss is for.
      .catch(() => { if (live) setVoices([]); });
    return () => { live = false; };
  }, [manifest, src]);

  /** Which clip is due — the track's value, 1-based, or 0 for none. */
  const track = manifest?.narration?.track;
  const clipNo = track ? Math.max(0, parseInt(String(values[track] ?? ''), 10) || 0) : 0;

  /**
   * Keep the voice in step with the track and with the transport.
   *
   * Everything here is driven off state rather than off events, so there is
   * exactly one place that decides what should be sounding: the clip for the
   * current step, playing when the simulation is.
   */
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    if (voice === 'off' || !clipNo || !clipDir.current) { el.pause(); return; }
    const want = `${clipDir.current}/${voice}/s${clipNo}.mp3`;
    if (el.src !== want) { el.src = want; el.currentTime = 0; }
    if (playing) void el.play().catch(() => { /* blocked until a gesture */ });
    else el.pause();
  }, [voice, clipNo, playing]);

  useEffect(() => () => { audio.current?.pause(); }, []);

  const toggleLabels = (on: boolean): void => {
    setLabelsChoice(on ? 'all' : 'few');
    send({ type: 'labels', all: on });
    try { localStorage.setItem('simplayer.labels', on ? 'all' : 'few'); } catch { /* not persisted */ }
  };

  const setParam = (key: string, value: number | string): void => {
    setValues(v => ({ ...v, [key]: value }));
    send({ type: 'set', key, value });
  };

  const applyPreset = (vals: Record<string, number | string>): void => {
    setValues(v => ({ ...v, ...vals }));
    send({ type: 'setMany', values: vals });
  };

  const caps = manifest?.capabilities;
  const params = manifest?.params ?? [];

  /* Stepping the narrated track. It is a mode in one explainer and a param in
     the other, which is the simulation's business, not the reader's — so the
     player asks which it is rather than making them agree. */
  const trackMode = manifest?.modes?.find(m => m.key === track);
  const trackParam = params.find(p => p.key === track);
  const trackMax = trackMode ? trackMode.options.length : trackParam ? trackParam.max : 0;
  const stepTo = (n: number): void => { if (track) setParam(track, trackMode ? String(n) : n); };

  /**
   * A clip has finished. Step on, if the simulation asked to be played
   * through — an eight-scene walkthrough that stops talking after scene one
   * is not a narration, it is a sample.
   *
   * Only while it is playing, and never past the end: the last clip stops and
   * leaves the final scene on the stage, which is where a walkthrough should
   * end up.
   */
  const onClipEnd = (): void => {
    if (!manifest?.narration?.advance || voice === 'off' || !playing) return;
    if (clipNo > 0 && clipNo < trackMax) stepTo(clipNo + 1);
  };

  return (
    <div
      className="simplayer"
      ref={root}
      data-orient={landscape ? 'landscape' : 'portrait'}
      data-panel={panel}
      data-panel-hidden={panelHidden ? 'true' : 'false'}
      data-full={full ? 'true' : 'false'}
      data-light={stageLight ? 'true' : undefined}
      data-fit={manifest?.fit}
    >
      {/* The name and the transport are two things, not one row of things.
          Kept in their own boxes because a phone held upright has width for
          one of them and not both: there the bar becomes two lines, the name
          on the first and these on the second, and neither has to shrink to
          fit beside the other. */}
      <div className="simplayer-top">
        {onBack && <button type="button" className="simplayer-gl simplayer-back" onClick={onBack} aria-label="Back">←</button>}
        <span className="simplayer-title">{manifest?.title ?? title}</span>
        <div className="simplayer-tools">
          {caps?.animates && (
            <button type="button" className="simplayer-gl" onClick={() => setPlaying(p => !p)} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? '⏸' : '▶'}
            </button>
          )}
          {caps?.animates && (
            <button type="button" className="simplayer-gl" onClick={() => send({ type: 'reset' })} aria-label="Reset">↺</button>
          )}
          {/* The benches' corner brackets (FullscreenIcon), not a box: filling
              the screen and hiding the controls are two different things, and
              a single glyph for both is what made the interface vanish when
              someone expected it to grow. One mark for full screen everywhere. */}
          {filling ? (
            onLeaveFull && (
              <button
                type="button"
                className="simplayer-gl"
                onClick={onLeaveFull}
                aria-label="Leave full screen"
                title="Leave full screen"
              ><FullscreenIcon exiting /></button>
            )
          ) : (
            <button
              type="button"
              className="simplayer-gl"
              onClick={toggleFullscreen}
              aria-label={full ? 'Leave full screen' : 'Full screen'}
            ><FullscreenIcon exiting={full} /></button>
          )}
          {/* Only when the panel is gone: while it is showing, its own ✕ is
              the way to dismiss it and a second control for the same thing is
              just another button. Rightmost, against the edge the panel
              returns along — and last, so pause, restart and full screen keep
              their order whether it is here or not. */}
          {panelHidden && (
            <button
              type="button"
              className="simplayer-gl"
              onClick={() => setPanelHidden(false)}
              aria-label="Show controls"
            >⚙</button>
          )}
        </div>
      </div>

      <div className="simplayer-stage" ref={stage}>
        <iframe ref={frame} src={src} title={title} allow="fullscreen" />
        {/* No controls of its own: the transport above already means play and
            pause, and a second pair for the sound would be two answers to the
            same question. */}
        <audio ref={audio} onEnded={onClipEnd} preload="none" hidden />


        {notice && (
          <div className="simplayer-notice">
            <span>{notice.text}</span>
            {notice.action && <button type="button" onClick={notice.action[1]}>{notice.action[0]}</button>}
          </div>
        )}

        {showProblems && problems.length > 0 && (
          <p className="simplayer-problems">manifest: {problems.join(' · ')}</p>
        )}

      </div>

      {manifest && (
        <div className="simplayer-panel">
          <div className="simplayer-panelhead">
            {/* Only where the simulation can actually go both ways. A control
                that cannot change anything is worse than no control: it reads
                as broken rather than as unavailable. */}
            {(caps?.themes ?? []).includes('light') && (
              <button
                type="button"
                className="simplayer-theme"
                onClick={toggleTheme}
                title={stageLight ? 'Read it on a dark ground' : 'Read it on a light ground'}
                aria-label={stageLight ? 'Dark' : 'Light'}
                aria-pressed={stageLight}
              >
                <ThemeIcon theme={stageLight ? 'light' : 'dark'} />
              </button>
            )}
            <button
              type="button"
              className="simplayer-grip"
              onClick={() => setPanel(p => (p === 'open' ? 'peek' : 'open'))}
              aria-expanded={panel === 'open'}
            >
              {/* The word, not the values.
                *
                * This line used to repeat every setting — "d 0.25 mm · a 0.08
                * mm · D 1.50 m · λ 520 nm" — which each slider already states
                * beside itself two lines below, and which the simulation draws
                * on the stage where a reader is actually looking. Three copies
                * of the same four numbers, and the smallest of them was the
                * one doing the least work. What this row is FOR is opening and
                * shutting the panel, so it says that. */}
              <span className="simplayer-vals">Controls</span>
              <span className="simplayer-chev" aria-hidden="true">⌃</span>
            </button>
            <button
              type="button"
              className="simplayer-close"
              onClick={() => setPanelHidden(true)}
              aria-label="Hide controls"
            >✕</button>
          </div>

          <div className="simplayer-body">
            {params.filter(p => Object.entries(p.when ?? {}).every(([k, v]) => values[k] === v)).map(p => (
              <div className="simplayer-p" key={p.key}>
                <div className="simplayer-r">
                  <label htmlFor={`sp_${p.key}`}>
                    {p.label}{p.symbol && <i>{p.symbol}</i>}
                  </label>
                  <output>{fmt(p, values[p.key] as number)}</output>
                </div>
                <input
                  id={`sp_${p.key}`} type="range"
                  min={p.min} max={p.max} step={p.step}
                  value={values[p.key] as number}
                  onChange={e => setParam(p.key, +e.target.value)}
                />
              </div>
            ))}

            {(manifest.modes ?? []).map(m => (
              <div className="simplayer-p" key={m.key}>
                <div className="simplayer-r"><label>{m.label}</label></div>
                <div className="simplayer-seg">
                  {m.options.map(o => {
                    const chosen = String(values[m.key] ?? '').split(',').filter(Boolean);
                    const on = m.multi ? chosen.includes(o.value) : values[m.key] === o.value;
                    return (
                      <button
                        type="button"
                        key={o.value}
                        aria-pressed={on}
                        onClick={() => {
                          if (!m.multi) { setParam(m.key, o.value); return; }
                          // Never all off: an empty set is a simulation with
                          // nothing in it, and the control that emptied it is
                          // then the only way back — from a blank stage.
                          const next = on ? chosen.filter(c => c !== o.value) : [...chosen, o.value];
                          if (next.length) {
                            // The manifest's order, not the order they were
                            // tapped in: the value is read by a simulation
                            // that lays things out in the order it declared.
                            setParam(m.key, m.options.filter(x => next.includes(x.value)).map(x => x.value).join(','));
                          }
                        }}
                      >{o.label}</button>
                    );
                  })}
                </div>
              </div>
            ))}

            {voices.length > 0 && (
              <div className="simplayer-p">
                <div className="simplayer-r">
                  <label htmlFor="sp_voice">Narration</label>
                </div>
                <select
                  id="sp_voice"
                  className="simplayer-voice"
                  value={voice}
                  onChange={e => {
                    const v = e.target.value;
                    setVoice(v);
                    // Start the step over, so the drawing and the voice begin
                    // together rather than the voice joining half way.
                    if (v !== 'off' && clipNo > 0) stepTo(clipNo);
                  }}
                >
                  <option value="off">Off</option>
                  {voices.map(v => (
                    <option key={v.key} value={v.key}>{v.label} · {v.register}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="simplayer-p">
              <label className="simplayer-switch">
                <input
                  type="checkbox"
                  checked={labelsAll}
                  onChange={e => toggleLabels(e.target.checked)}
                />
                Show all labels
              </label>
            </div>

            {(manifest.presets ?? []).length > 0 && (
              <div className="simplayer-p">
                <div className="simplayer-r"><label>Try</label></div>
                <div className="simplayer-seg">
                  {manifest.presets!.map(pr => (
                    <button type="button" key={pr.label} onClick={() => applyPreset(pr.values)}>{pr.label}</button>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

export default SimulationPlayer;
